/**
 * useWebOtp — Android Chrome auto-fill for 6-digit SMS OTPs.
 *
 * Uses the WebOTP API (`navigator.credentials.get({otp:{transport:['sms']}})`)
 * which lets the browser listen for an incoming SMS that ends with the
 * `@<origin> #<code>` sentinel and hand the code to the page automatically —
 * no clipboard, no notification tray tap, no manual paste.
 *
 * Requirements (already registered on the KAPSYSTEM DLT template):
 *   1. SMS body ends with a line: `@admissioncrafts.com #123456`
 *   2. Site is served over HTTPS
 *   3. User agent supports WebOTP (Chrome ≥84 on Android)
 *
 * Non-Android / desktop browsers are a graceful no-op — the input's
 * `autoComplete="one-time-code"` handles them via the SMS keyboard-toolbar
 * suggestion chip. So this hook adds zero downside.
 */
import { useEffect } from "react";

export default function useWebOtp({ enabled, onCode }) {
  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === "undefined") return undefined;
    if (!("OTPCredential" in window)) return undefined;

    // AbortController so we can cancel the WebOTP watcher if the user
    // navigates away or submits manually before the SMS arrives.
    const ac = new AbortController();

    // Fire-and-forget — the promise resolves only when Android delivers
    // a matching SMS (or when we abort).
    navigator.credentials
      .get({
        otp: { transport: ["sms"] },
        signal: ac.signal,
      })
      .then((otp) => {
        if (otp && otp.code) onCode(String(otp.code).replace(/\D/g, "").slice(0, 6));
      })
      .catch(() => {
        // AbortError or user denied prompt — silent ignore.
      });

    return () => ac.abort();
  }, [enabled, onCode]);
}
